import axios from "axios";
import ApiQueryService from "../api/query/ApiQueryService";
import { IDTResourceCollection } from "../types/XSifterGeneralTypes";
import { CodingRefinementRequest, IDiscoverTextProject, IDiscoverTextProjectArchives, IQueryResponseWithId, IRunningTasks, IRunningTaskStatusItem, ISampledPostItemCollection, ISampleQueryRound, ISavedQueries, IXSifterCreateSavedQueryResponse, IXSifterEstimateResponse, IXSifterQuery, IXSifterQueryRoot, IXSifterSamplingProgress, IXSifterSavedQuery, IXSifterSavedQueryListRequest, IUserDescriptionArchiveOptions, QueryRefinementResult, QueryRefinementResultList, SubmitQueryResponse, ISampledPostItemComment } from "../types/XSifterQueries";
import UserAuthService from "./UserAuthService";
import { RuleGroupType } from "react-querybuilder";

export const TWEET_HTML_UNAVAILABLE_MESSAGE = "<i>Could not resolve tweet HTML</i>";
export const TWEET_HTML_BLOCKED_MESSAGE = "<i>Tweet embed request was blocked</i>";

export default class XSifterQueryService {
    private userAuthService = UserAuthService.getInstance();
    private queryService = new ApiQueryService();
    private _currentRunningJobs: IRunningTaskStatusItem[] = [];
    private _checkRunningJobsTimeout: any;
    private _queryTraceId: string | null = null;

    public getQueryTraceId = (): string | null => {
        return this._queryTraceId;
    }

    public setQueryTraceId = (queryTraceId: string | null): void => {
        this._queryTraceId = queryTraceId;
    }

    public generateNewQueryId = (): string => {
        const chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
        var result = "";
        for (var i = 32; i > 0; --i) {
            result += chars[Math.round(Math.random() * (chars.length - 1))];
        }
        return result;
    }

    public async getEstimate(query: IXSifterQueryRoot | null) : Promise<IXSifterEstimateResponse> {
        return new Promise<IXSifterEstimateResponse>((resolve, reject) => {
            if (query === null) {
                reject("no query supplied");
                return;
            }
            
            if (!this.userAuthService.isLoggedIn()) {
                reject("user is not logged in");
                return;
            }

            this.queryService.getEstimate(this.userAuthService.getToken() ?? "", this._queryTraceId, query)
            .then((response) => {
                this._queryTraceId = response.currentQueryId;
                resolve(response);
            })
            .catch((error) => {
                reject(error);
            })
        });
    }

    public async getProjectList() : Promise<IDTResourceCollection<IDiscoverTextProject>> {
        return this.queryService.getDiscoverTextProjects(this.userAuthService.getToken() ?? "");
    }

    public async getDiscoverTextArchivesForProject(projectId: number): Promise<IDiscoverTextProjectArchives> {
        return this.queryService.getDiscoverTextArchivesForProject(this.userAuthService.getToken() ?? "", projectId);
    }

    public async saveNewQuery(query: IXSifterQuery, queryTitle: string, estimateCount: number): Promise<IXSifterCreateSavedQueryResponse> {
        return this.queryService.saveNewQuery(this.userAuthService.getToken() ?? "", this._queryTraceId, query, queryTitle, estimateCount);
    }

    public async updateSavedQuery(queryId: string, query: IXSifterQuery, queryTitle: string, estimateCount: number): Promise<IXSifterCreateSavedQueryResponse> {
        return this.queryService.updateSavedQuery(
            this.userAuthService.getToken() ?? "",
            this._queryTraceId,
            queryId,
            query,
            queryTitle,
            estimateCount
        );
    }

    public async getSavedQuery(queryId: string): Promise<IXSifterSavedQuery | null> {
        return this.queryService.getSavedQuery(this.userAuthService.getToken() ?? "", queryId);
    }

    public async deleteSavedQuery(queryId: string): Promise<IXSifterSavedQuery> {
        return this.queryService.deleteSavedQuery(this.userAuthService.getToken() ?? "", queryId);
    }

    public async getTopSavedQueries() : Promise<ISavedQueries> {
        return this.queryService.getSavedQueries(this.userAuthService.getToken() ?? "", {
            limit: 10,
            offset: 0,
            submitted: true,
        });
    }

    public async getSavedQueries(request?: IXSifterSavedQueryListRequest) : Promise<ISavedQueries> {
        return this.queryService.getSavedQueries(this.userAuthService.getToken() ?? "", request);
    }

    public async translateSavedQuery(queryId: string): Promise<string> {
        return this.queryService.translateSavedQuery(this.userAuthService.getToken() ?? "", queryId);
    }

    public async translateTwitterQuery(twitterQuery: string): Promise<RuleGroupType | null | string> {
        return this.queryService.translateTwitterQuery(this.userAuthService.getToken() ?? "", twitterQuery);
    }

    public async submitFetch(savedQueryId: string, projectId: number, archiveName?: string, existingArchiveId?: number, userDescOptions?: IUserDescriptionArchiveOptions) : Promise<SubmitQueryResponse> {
        return this.queryService.submitFetch(this.userAuthService.getToken() ?? "", this._queryTraceId, savedQueryId, projectId, archiveName, existingArchiveId, userDescOptions);
    }

    public async getRunningTasks() : Promise<IRunningTasks> {
        return this.queryService.getRunningTasks(this.userAuthService.getToken() ?? "");
    }

    public async cancelRunningJob(taskId: string) : Promise<boolean> {
        return this.queryService.cancelRunningJob(this.userAuthService.getToken() ?? "", taskId);
    }

    public async getRenderedTweetHtml(itemId: string, itemUsername: string): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            let url = `https://publish.x.com/oembed?hide_thread=true&omit_script=true&url=https://x.com/${itemUsername}/status/${itemId}`;
            axios.get(url)
                .then((response) => {
                    resolve(response.data.html);
                })
                .catch((error) => {
                    // an HTTP status means X answered and the post is gone; no response at all means
                    // the request was blocked (e.g. tracking protection) or the network failed, which
                    // says nothing about whether the post still exists
                    resolve(error?.response ? TWEET_HTML_UNAVAILABLE_MESSAGE : TWEET_HTML_BLOCKED_MESSAGE);
                })
        });
    }

    public async getCodingRound(codingRoundId: string): Promise<ISampleQueryRound> {
        return this.queryService.getCodingRound(this.userAuthService.getToken() ?? "", this._queryTraceId, codingRoundId);
    }

    public async getOrCreateCodingRound(savedQueryId: string): Promise<ISampleQueryRound> {
        return this.queryService.getOrCreateCodingRound(this.userAuthService.getToken() ?? "", this._queryTraceId, savedQueryId);
    }

    public async getSampledItemsForCoding(codingRoundId: string): Promise<ISampledPostItemCollection> {
        return this.queryService.getSampledItemsForCoding(this.userAuthService.getToken() ?? "", codingRoundId);
    }

    public async fetchSampleQueryItems(codingRoundId: string, abortController: AbortController | null, onMessage: (message: IXSifterSamplingProgress) => void, onError: (error: string) => void, onComplete: ()=> void)
    {
        const run = async () => {
            try {
                const response = await this.queryService.doFetchSampleQueryItems(this.userAuthService.getToken() ?? "", this._queryTraceId, codingRoundId, abortController);
                if (!response.ok) {
                    throw new Error(`Sampling request failed (${response.status} ${response.statusText})`);
                }
                if (!response.body) {
                    throw new Error("Sampling response did not include a readable stream.");
                }
                const reader = response.body.getReader();
                const decoder = new TextDecoder('utf-8');
                let done = false;
                // Network chunk boundaries are arbitrary (and differ between browsers), so a
                // message or even the delimiter may be split across reads; buffer until complete.
                const delimiter = "\n---ENDDATA---\n";
                let buffer = "";

                const dispatch = (raw: string) => {
                    const text = raw.trim();
                    if (text.startsWith('data:')) {
                        onMessage(JSON.parse(text.substring(5)) as IXSifterSamplingProgress);
                    }
                };

                while (reader && !done) {
                    const {value, done: streamDone } = await reader.read();
                    done = streamDone;

                    if (value) {
                        buffer += decoder.decode(value, { stream: !done });
                    }

                    let delimiterIndex = buffer.indexOf(delimiter);
                    while (delimiterIndex !== -1) {
                        dispatch(buffer.substring(0, delimiterIndex));
                        buffer = buffer.substring(delimiterIndex + delimiter.length);
                        delimiterIndex = buffer.indexOf(delimiter);
                    }
                }

                // a final message may arrive without a trailing delimiter
                if (buffer.trim().length > 0) {
                    dispatch(buffer);
                }
                return true;
            } catch (error: any) {
                if (abortController?.signal.aborted) {
                    onError("--cancelled--");
                    return true;
                }
                onError(error);
                return false;
            } finally {
                onComplete();
            }
        }

        run();
        return () => {};        
    }

    public async scoreSampledItem(codingRoundId: string, codedSampleId: string, itemWeight: number): Promise<boolean>
    {
        return this.queryService.scoreSampledItem(this.userAuthService.getToken() ?? "", codingRoundId, codedSampleId, itemWeight);
    }

    public async getInitialQueryFromDescription(queryDescription: string): Promise<QueryRefinementResultList> {
        return this.queryService.getInitialQueryFromDescription(this.userAuthService.getToken() ?? "", this._queryTraceId, { queryDescription: queryDescription});
    }

    public async submitForRefinement(request: CodingRefinementRequest): Promise<QueryRefinementResultList> {
        return this.queryService.submitForRefinement(this.userAuthService.getToken() ?? "", this._queryTraceId, request);
    }

    public async applyQueryRefinement(savedQueryId: string, refinement: QueryRefinementResult): Promise<IQueryResponseWithId> {
        return this.queryService.applyQueryRefinement(this.userAuthService.getToken() ?? "", this._queryTraceId, savedQueryId, refinement);
    }

    /// sample item comments

    public async getCommentsForSampledItem(codingRoundId: string, codedSampleId: string): Promise<ISampledPostItemComment[]> {
        return this.queryService.getCommentsForSampledItem(this.userAuthService.getToken() ?? "",this._queryTraceId, codingRoundId, codedSampleId);
    }

    public async postCommentForSampledItem(codingRoundId: string, codedSampleId: string, comment: string): Promise<ISampledPostItemComment> {
        return this.queryService.postCommentForSampledItem(this.userAuthService.getToken() ?? "",this._queryTraceId, codingRoundId, codedSampleId, comment);
    }

    public async editCommentForSampledItem(codingRoundId: string, codedSampleId: string, commentId: string, comment: string): Promise<ISampledPostItemComment> {
        return this.queryService.editCommentForSampledItem(this.userAuthService.getToken() ?? "",this._queryTraceId, codingRoundId, codedSampleId, commentId, comment);
    }

    public async deleteCommentForSampledItem(codingRoundId: string, codedSampleId: string, commentId: string): Promise<boolean> {
        return this.queryService.deleteCommentForSampledItem(this.userAuthService.getToken() ?? "",this._queryTraceId, codingRoundId, codedSampleId, commentId);
    }
}
